/**
 * i18n — SKNOTIFICATION-GLOBAL-CR-001 UICC-001 "kit adoption". No
 * `@softknack/globalization-kit` package exists (confirmed absent), so this
 * mirrors the shape sk-notification-api's OWN i18n loader already uses
 * (src/i18n/i18nLoader.ts: flat key -> string bundles, `{{var}}`
 * interpolation, missing-key falls back to `en`) — same convention across
 * the stack, not a different one invented for the frontend.
 *
 * Only an `en` bundle exists: no translation content has been authored for
 * ta/hi/ar. This still does real work — every user-facing string in the 5
 * pre-existing screens is now a key, not a literal, so adding a language
 * later is a content task (a new bundle file), not an engineering one.
 */
import en from './en';

type Bundle = Record<string, string>;
const BUNDLES: Record<string, Bundle> = { en };

export const SUPPORTED_LOCALES = Object.keys(BUNDLES);
const DEFAULT_LOCALE = 'en';

function bundleFor(locale?: string): Bundle {
  if (locale) {
    // Console locale is a full tag like 'en-IN'/'ar-AE' (AppContext default);
    // the bundle key is just the language subtag.
    const lang = locale.split('-')[0];
    if (BUNDLES[lang]) return BUNDLES[lang];
  }
  return BUNDLES[DEFAULT_LOCALE];
}

function interpolate(template: string, data: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key) => {
    const v = data[key];
    return v === undefined || v === null ? '' : String(v);
  });
}

/** Translate `key`, interpolating `{{var}}` placeholders from `data`. Falls back to the raw key if missing everywhere — visible-but-not-crashing, same policy as the backend loader minus the throw (a console string is not worth a hard failure). */
export function t(key: string, data: Record<string, unknown> = {}, locale?: string): string {
  const bundle = bundleFor(locale);
  const raw = bundle[key] ?? BUNDLES[DEFAULT_LOCALE][key];
  if (!raw) return key;
  return interpolate(raw, data);
}
